import type { Failure } from "@/modules/shared/failure";
import { SYSTEM_ERROR, type FormState } from "@/modules/shared/forms";
import { serviceLocationLabel } from "@/modules/shared/serviceLocation";
import { GUARD_COPY } from "../domain/bookingState";

/**
 * The **web's** copy for every code a Consumer booking command can refuse with
 * (R22 T12).
 *
 * Lifted out of `./consumer.ts` for the reason the auth and business copy maps
 * were lifted out of theirs: that file is `"use server"`, where every export
 * must be an async function, so these sentences could be neither shared with
 * the static web app nor pinned by a test. Every sentence is unchanged.
 *
 * **The state-machine refusals are not restated here.** Eight of them come
 * straight from `GUARD_COPY`, which is the state machine's own vocabulary
 * (`domain/bookingState.ts`): a second wording for "that request has already
 * lapsed" would be a second place to disagree with the transition table.
 */
export function consumerCopyFor(failure: Failure): string {
  switch (failure.code) {
    case "duplicate_request":
      return "You already have a booking for this time.";
    case "block_unavailable":
      return "This time is no longer available.";
    case "slot_taken":
      return "This time is already booked. Please pick another.";
    case "past_start":
      return "That time has already started.";
    case "not_found":
      return "This booking could not be found.";
    case "service_unavailable":
      return "That service is no longer offered.";
    case "location_unavailable":
      // H24: the chosen Service Location isn't offered here / for this time.
      return `${serviceLocationLabel(
        failure.serviceLocation ?? "in_clinic",
      )} visits aren't available for this time.`;
    case "home_address_required":
      return "Enter the address where the practitioner should visit.";
    case "malformed_input":
      return "That request was malformed. Please try again.";
    case "same_block":
      return "That is already your booking's time.";
    case "booking_not_open":
    case "booking_expired":
    case "booking_not_active":
    case "booking_already_complete":
    case "cancellation_window":
    case "reschedule_window":
    case "no_pending_suggestion":
    case "suggestion_expired":
      return GUARD_COPY[failure.code];
    default:
      return SYSTEM_ERROR;
  }
}

/**
 * The same refusal as the `FormState` a screen renders.
 *
 * Every one of these is a banner rather than a field error, and that is not an
 * oversight: none of them is about something the reader typed. "This time is
 * already booked" is about the world having changed under a choice they made
 * correctly, and hanging it off a form control would say otherwise.
 */
export function formStateFor(failure: Failure): FormState {
  return { formError: consumerCopyFor(failure) };
}
